import React, { useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Plus, ChartBar, Flag, Warning,
} from '@phosphor-icons/react';
import {
  format, parseISO, isSameDay, differenceInDays, addDays,
} from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_COLORS = {
  completed: { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', label: 'Terminée', hex: '#10B981' },
  in_progress: { bar: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', label: "En cours d'execution", hex: '#3B82F6' },
  pending: { bar: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-50', label: 'A realiser', hex: '#FBBF24' },
  delayed: { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', label: 'En retard critique', hex: '#EF4444' },
};

function MilestoneDiamond({ task, leftPct, onClick }) {
  return (
    <div className="absolute cursor-pointer group/ms" style={{ left: `${leftPct}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
      onClick={onClick} data-testid={`gantt-milestone-${task.id}`}>
      <div className="relative">
        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#FF6B00] rotate-45 rounded-sm shadow-lg border-2 border-white" />
        <Flag size={14} weight="fill" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white -rotate-45" />
      </div>
      <div className="absolute -top-12 left-1/2 -translate-x-1/2 hidden group-hover/ms:block bg-[#111827] text-white text-[11px] px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap z-20 border border-white/10">
        <Flag size={12} weight="fill" className="inline mr-1 text-[#FF6B00]" />
        <span className="font-bold">{task.title}</span> — {task.start_date && format(parseISO(task.start_date), 'dd/MM/yyyy')}
      </div>
    </div>
  );
}

function DelayBadge({ compact }) {
  if (compact) {
    return <Warning size={14} weight="fill" className="text-red-500 animate-pulse flex-shrink-0" />;
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold animate-pulse whitespace-nowrap" data-testid="delay-badge">
      <Warning size={10} weight="fill" /> RETARD CRITIQUE
    </span>
  );
}

function getProgressBadgeStyle(progress) {
  if (progress >= 75) return 'bg-emerald-100 text-emerald-700';
  if (progress >= 40) return 'bg-orange-100 text-[#FF6B00]';
  return 'bg-gray-100 text-[#6B7280]';
}

function getProgressTextColor(progress) {
  if (progress >= 75) return 'text-emerald-400';
  if (progress >= 40) return 'text-orange-400';
  return 'text-white';
}

function TodayLine({ ganttData }) {
  const todayOffset = differenceInDays(new Date(), ganttData.timeStart);
  if (todayOffset < 0 || todayOffset > ganttData.totalDays) return null;
  return <div className="absolute top-0 bottom-0 w-0.5 bg-[#FF6B00] z-[5] opacity-50"
    style={{ left: `${(todayOffset / ganttData.totalDays) * 100}%` }} />;
}

export function GanttChart({ filteredTasks, ganttData, timeLabels, onEditTask, onCreateSheet }) {
  if (ganttData.lots.length === 0 && ganttData.milestones.length === 0) {
    return (
      <Card className="border border-dashed">
        <CardContent className="py-12 text-center">
          <ChartBar size={48} className="mx-auto mb-3 text-gray-300" />
          <h3 className="font-bold text-[#111827] mb-1">Aucune tâche avec dates</h3>
          <p className="text-sm text-[#6B7280] mb-4">Creez des tâches avec dates de debut et fin pour visualiser le diagramme de Gantt</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => onCreateSheet()} className="bg-[#FF6B00] hover:bg-orange-700">
              <Plus size={16} className="mr-1.5" /> Nouvelle tâche
            </Button>
            <Button onClick={() => onCreateSheet(null, true)} variant="outline" className="border-[#FF6B00] text-[#FF6B00]">
              <Flag size={16} className="mr-1.5" weight="fill" /> Jalon
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden shadow-md" data-testid="gantt-chart">
      <CardContent className="p-0">
        <div className="overflow-x-auto -mx-1 px-1">
          <div style={{ minWidth: Math.max(600, ganttData.totalDays * 28 + 180) }}>
            {/* Time header */}
            <div className="flex border-b-2 border-gray-200 bg-gray-50 sticky top-0 z-10">
              <div className="w-[140px] sm:w-[200px] min-w-[140px] sm:min-w-[200px] p-2 sm:p-3 text-xs font-bold text-[#6B7280] border-r border-gray-200">
                Lot / Tâche
              </div>
              <div className="flex-1 flex">
                {timeLabels.map((date) => {
                  const isToday = isSameDay(date, new Date());
                  return (
                    <div key={date.toISOString()} className={`text-center text-[10px] py-2 border-r border-gray-100 ${isToday ? 'bg-[#FF6B00]/10 font-bold text-[#FF6B00]' : 'text-[#9CA3AF]'}`}
                      style={{ width: `${(ganttData.totalDays > 60 ? 7 : ganttData.totalDays > 30 ? 3 : 1) / ganttData.totalDays * 100}%`, minWidth: ganttData.totalDays > 60 ? 50 : 24 }}>
                      {format(date, ganttData.totalDays > 60 ? 'dd/MM' : 'dd', { locale: fr })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gantt rows by lot */}
            {ganttData.lots.map((lot, lotIdx) => (
              <div key={lot.name} data-testid={`gantt-lot-${lotIdx}`}>
                <div className="flex border-b border-gray-200 bg-[#FF6B00]/5">
                  <div className="w-[140px] sm:w-[200px] min-w-[140px] sm:min-w-[200px] p-2 pl-3 border-r border-gray-200">
                    <span className="text-xs font-bold text-[#111827] uppercase tracking-wide">{lot.name}</span>
                    <span className="text-[10px] text-[#9CA3AF] ml-2">{lot.tasks.length}</span>
                  </div>
                  <div className="flex-1" />
                </div>
                {lot.tasks.map(task => {
                  const taskStart = parseISO(task.start_date);
                  const taskEnd = parseISO(task.end_date);
                  const offsetDays = differenceInDays(taskStart, ganttData.timeStart);
                  const durationDays = Math.max(differenceInDays(taskEnd, taskStart) + 1, 1);
                  const leftPct = (offsetDays / ganttData.totalDays) * 100;
                  const widthPct = (durationDays / ganttData.totalDays) * 100;
                  const sc = STATUS_COLORS[task.status] || STATUS_COLORS.pending;
                  const progress = task.progress || (task.status === 'completed' ? 100 : 0);
                  const isDelayed = task.status === 'delayed';

                  return (
                    <div key={task.id}
                      className={`flex border-b border-gray-100 group transition-colors ${isDelayed ? 'bg-red-50/60 hover:bg-red-50' : 'hover:bg-orange-50/30'}`}
                      data-testid={`gantt-task-${task.id}`}>
                      <div className="w-[140px] sm:w-[200px] min-w-[140px] sm:min-w-[200px] p-2 pl-4 sm:pl-5 border-r border-gray-200 flex items-center gap-1.5 cursor-pointer"
                        onClick={() => onEditTask(task)}>
                        {isDelayed ? <DelayBadge compact /> : <div className={`w-3 h-3 rounded-full flex-shrink-0 ${sc.bar}`} />}
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] sm:text-xs font-semibold text-[#111827] truncate">{task.title}</p>
                          <div className="flex items-center gap-1">
                            <p className="text-[10px] text-[#9CA3AF] truncate">{task.responsible || ''}</p>
                            {task.responsible && <span className="text-[10px] text-[#D1D5DB]">·</span>}
                            <span className="text-[10px] text-[#9CA3AF]">{durationDays}j</span>
                          </div>
                        </div>
                        {isDelayed ? <DelayBadge /> : (
                          <div className={`flex-shrink-0 px-1.5 py-0.5 rounded-md ${getProgressBadgeStyle(progress)}`}>
                            <span className="text-xs font-black">{progress}%</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 relative" style={{ minHeight: 56 }}>
                        <TodayLine ganttData={ganttData} />
                        <div className="absolute top-2 cursor-pointer group/bar"
                          style={{ left: `${leftPct}%`, width: `${widthPct}%`, minWidth: 24 }}
                          onClick={() => onEditTask(task)}>
                          <div className={`h-12 rounded-xl ${sc.bar} opacity-20`} />
                          <div className={`absolute top-0 left-0 h-12 rounded-xl ${sc.bar} transition-all duration-500 ${isDelayed ? 'shadow-md shadow-red-200' : 'shadow-sm'}`}
                            style={{ width: `${Math.max(progress, 3)}%` }} />
                          {isDelayed && (
                            <div className="absolute inset-0 h-12 rounded-xl overflow-hidden opacity-10"
                              style={{ backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, white 4px, white 8px)' }} />
                          )}
                          {widthPct > 5 && (
                            <div className="absolute inset-0 flex items-center px-3 overflow-hidden">
                              <span className="text-[11px] sm:text-xs font-bold text-white truncate drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
                                {task.title} — {progress}% — {durationDays}j
                              </span>
                            </div>
                          )}
                          <div className="absolute -top-14 left-0 hidden group-hover/bar:flex items-center gap-2 bg-[#111827] text-white text-[11px] px-3 py-2 rounded-lg shadow-xl whitespace-nowrap z-20 border border-white/10">
                            {isDelayed && <Warning size={14} weight="fill" className="text-red-400" />}
                            <span className="font-bold">{task.title}</span>
                            <span className="text-white/60">|</span>
                            <span className={`font-bold ${getProgressTextColor(progress)}`}>{progress}%</span>
                            <span className="text-white/60">|</span>
                            <span>{durationDays}j</span>
                            <span className="text-white/60">|</span>
                            <span>{format(taskStart, 'dd/MM')} - {format(taskEnd, 'dd/MM')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Milestones row */}
            {ganttData.milestones.length > 0 && (
              <div data-testid="gantt-milestones-row">
                <div className="flex border-b border-gray-200 bg-orange-50/50">
                  <div className="w-[140px] sm:w-[200px] min-w-[140px] sm:min-w-[200px] p-2 pl-3 border-r border-gray-200 flex items-center gap-2">
                    <Flag size={14} weight="fill" className="text-[#FF6B00]" />
                    <span className="text-xs font-bold text-[#FF6B00] uppercase tracking-wide">Jalons</span>
                    <span className="text-[10px] text-[#9CA3AF]">{ganttData.milestones.length}</span>
                  </div>
                  <div className="flex-1 relative" style={{ minHeight: 52 }}>
                    <TodayLine ganttData={ganttData} />
                    {ganttData.milestones.map(ms => {
                      const msDate = parseISO(ms.start_date);
                      const offsetDays = differenceInDays(msDate, ganttData.timeStart);
                      const leftPct = (offsetDays / ganttData.totalDays) * 100;
                      return <MilestoneDiamond key={ms.id} task={ms} leftPct={leftPct} onClick={() => onEditTask(ms)} />;
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 p-3 border-t-2 border-gray-100 bg-gray-50">
          {Object.entries(STATUS_COLORS).map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className={`w-4 h-4 rounded ${val.bar}`} />
              <span className="text-[11px] font-semibold text-[#4B5563]">{val.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 bg-[#FF6B00] rotate-45 rounded-sm" style={{ transform: 'rotate(45deg) scale(0.6)' }} />
            <span className="text-[11px] font-semibold text-[#FF6B00]">Jalon</span>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <div className="w-0.5 h-4 bg-[#FF6B00] rounded-full" />
            <span className="text-[11px] font-bold text-[#FF6B00]">Aujourd'hui</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
